import React, {useEffect} from 'react';
import {startOfDay} from "date-fns";
import useData from "../../../hooks/useData";
import {CustomProvider, DatePicker, DateRangePicker} from "rsuite";
import {nlNL} from "rsuite/locales";
import {Box, Divider, Grid, Typography, useTheme, IconButton} from "@mui/material";
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import {Statistics} from "./statistics/statistics";
import {Card} from "../../../components/card";
import {
  ConsumptionChart,
  FlexChart,
  GridChart,
  ImbalancePricesChart,
  ProductionChart,
  SOCChart
} from "../charts/elionCharts";
import 'rsuite/dist/rsuite.min.css'; // Style for date picker
import {CustomTheme} from "../../../config/theme/theme";
import {useTranslation} from 'react-i18next';
import {ChartCard} from '../../../components/chartCard';
import {isMobile} from 'react-device-detect';
import LocationSvg from "../../../assets/rebranding/icons/icon_location.svg";
import BatterySvg from "../../../assets/rebranding/icons/icon_battery.svg";
import LiveSvg from "../../../assets/rebranding/icons/icon_live_battery.svg";
import CalendarSvg from "../../../assets/images/calendar.svg";
import FlowAnimation from "../flowAnimation/flowAnimation";
import {RangeChip} from '../../../components/rangeChip';

export default function Technical() {
  const {
    api,
    activeSmartBox,
    live,
    data,
    setData,
    setLive,
    lastFetch,
    setLastFetch,
    isFetching,
    setIsFetching,
    liveUpdateTime,
    setLiveUpdateTime,
  } = useData();
  const {siteApi} = api || {};
  const [selectedDate, setSelectedDate] = React.useState(new Date())
  const [pricesData, setPricesData] = React.useState(null);
  const [isEpexControlled, setIsEpexControlled] = React.useState<boolean>(false);
  const [isOptimizerFetching, setIsOptimizerFetching] = React.useState<boolean>(true);
  const [confOptimizer, setConfOptimizer] = React.useState<string | null>(null);
  const [actOptimizer, setActOptimizer] = React.useState<string | null>(null);

  function goToPreviousDay() {
    const prevDay = new Date(selectedDate);
    prevDay.setDate(prevDay.getDate() - 1);
    setSelectedDate(prevDay);
  }

  function goToNextDay() {
    const nextDay = new Date(selectedDate);
    nextDay.setDate(nextDay.getDate() + 1);
    const today = new Date();
    if (startOfDay(nextDay) <= startOfDay(today)) {
      setSelectedDate(nextDay);
    }
  }

  function isToday(date: Date): boolean {
    const today = new Date();
    return date.toDateString() === today.toDateString();
  }

  const [pickerOpen, setPickerOpen] = React.useState(false)
  const fetchingLiveRef = React.useRef(false);
  const lastPricesFetch = React.useRef(0);
  const debounceTimerRef = React.useRef<NodeJS.Timeout | null>(null);
  const {afterToday} = DateRangePicker;

  const {t} = useTranslation();

  const theme: CustomTheme = useTheme();

  React.useEffect(() => {
    // Clear any pending debounced fetch
    if (debounceTimerRef.current) {
      clearTimeout(debounceTimerRef.current);
    }

    if (activeSmartBox && selectedDate !== null) {
      setData(null);
      setPricesData(null);
      
      // Debounce the data fetching - wait 300ms after last date change
      debounceTimerRef.current = setTimeout(() => {
        if (!isFetching.current) {
          fetchData(true);
          fetchPricesData(true);
          fetchEpexControl();
        }
      }, 300);

      const dataInterval = setInterval(() => {
        fetchData(false);
        fetchPricesData(false);
      }, 30000);
      
      return () => {
        clearInterval(dataInterval);
        if (debounceTimerRef.current) {
          clearTimeout(debounceTimerRef.current);
        }
      };
    }
  }, [selectedDate, activeSmartBox]);

  useEffect(() => {
    setLive(null)
    fetchLiveData()
    fetchOptimizer()
    const liveDataInterval = setInterval(() => {
      fetchLiveData()
    }, 5000)
    return () => clearInterval(liveDataInterval)
  }, [activeSmartBox]);

  async function fetchEpexControl() {
    if (activeSmartBox?.siteId) {
      try {
        const response = await api.getAxiosInstance().get(`/api/sites/${activeSmartBox.siteId}/epex-control`, {
          params: { date: Math.floor(selectedDate.valueOf() / 1000) }
        });
        setIsEpexControlled(response.data.is_epex || false);
      } catch (e) {
        console.error(e);
        setIsEpexControlled(false);
      }
    }
  }

  async function fetchOptimizer() {
    if (activeSmartBox?.siteId) {
      setIsOptimizerFetching(true);
      try {
        const response = await api.getAxiosInstance().get(`/api/sites/${activeSmartBox.siteId}/optimizer`);
        setConfOptimizer(response.data.conf_optimizer || null);
        setActOptimizer(response.data.act_optimizer || null);
      } catch (e) {
        console.error(e);
        setConfOptimizer(null);
        setActOptimizer(null);
      } finally {
        setIsOptimizerFetching(false);
      }
    }
  }

  async function fetchData(forced) {
    if (isFetching.current || !api || !activeSmartBox) return

    const now = Date.now()
    if (!forced) {
      if (now - lastFetch.current < 30000) {
        return
      }
    }
    setLastFetch(now)
    setIsFetching(true)

    let start: Date = startOfDay(new Date(selectedDate))
    let end: Date = new Date(selectedDate)
    if (start.toDateString() !== (new Date(now)).toDateString()) {
      end.setHours(23, 59, 59, 999)
    }
    if (activeSmartBox?.siteId) {
      siteApi.getDataApiSitesSiteIdMeteringGet(activeSmartBox.siteId, Math.floor(start.valueOf() / 1000), Math.floor(end.valueOf() / 1000)).then((response: any) => {
        // let data = response.data.readings.filter(point => {
        //   return new Date(point.utctime * 1000) > new Date("2024-09-14 07:00:00") && new Date(point.utctime * 1000) < new Date("2024-09-14 20:00:00")
        // });
        // response.data.readings = data;
        setData(response.data);
      }).catch((e) => {
        console.error(e);
      }).finally(() => {
        setIsFetching(false);
      })
    }
  }

  async function fetchPricesData(forced) {
    if (!api || !activeSmartBox) return

    const now = Date.now()
    if (!forced) {
      if (now - lastPricesFetch.current < 30000) {
        return
      }
    }
    lastPricesFetch.current = now

    let start: Date = startOfDay(new Date(selectedDate))
    let end: Date = new Date(selectedDate)
    // Always fetch until end of day for EPEX prices (available for whole day)
    end.setHours(23, 59, 59, 999)
    
    if (activeSmartBox?.siteId) {
      siteApi.getDataApiSitesSiteIdMeteringGet(activeSmartBox.siteId, Math.floor(start.valueOf() / 1000), Math.floor(end.valueOf() / 1000)).then((response: any) => {
        setPricesData(response.data);
      }).catch((e) => {
        console.error(e);
      })
    }
  }

  async function fetchLiveData() {
    if (fetchingLiveRef.current == true || !api || !activeSmartBox) {
      return;
    }
    fetchingLiveRef.current = true;
    if (activeSmartBox?.siteId) {
      siteApi.getLiveDataApiSitesSiteIdLiveGet(activeSmartBox.siteId).then((response: any) => {
        setLive(response.data);
        setLiveUpdateTime(new Date())
        fetchingLiveRef.current = false;
      }).catch((e) => {
        console.error(e)
      }).finally(() => {
        fetchingLiveRef.current = false;
      })
    }
  }

  let productionValue = live && live.production !== null ? live?.production : 0;
  let flexValue = live && live.flex !== null ? live?.flex : 0;
  let consumptionValue = live && live.consumption !== null ? live?.consumption : 0;
  let gridValue = live && live.grid !== null ? live?.grid : 0;

  let GRID_OFFTAKE = Math.max(gridValue, 0);
  let GRID_INJECT = Math.max(-gridValue, 0);
  let CHARGE = Math.max(flexValue, 0);
  let DISCHARGE = Math.max(-flexValue, 0);
  let S1 = round(Math.min(productionValue, CHARGE), 2);
  let S2 = round(Math.min(productionValue - S1, consumptionValue), 2);
  let S3 = round(productionValue - S1 - S2, 2);
  let C2 = round(Math.min(consumptionValue - S2, DISCHARGE), 2);
  let C3 = round(consumptionValue - S2 - C2, 2);
  let G1 = round(GRID_OFFTAKE - C3, 2);
  let G2 = round(GRID_INJECT - S3, 2);

  function showVertical() {
    return gridValue !== 0 && S3 > 0 ? 1 : 0;
  }

  function showHorizontal() {
    return (C2 > 0) ? 1 : 0;
  }

  function showLeftTop() {
    return (S1 > 0) ? -1 : 0;
  }

  function showRightTop() {
    return (S2 > 0) ? 1 : 0;
  }

  function showBottomLeft() {
    return gridValue !== 0 && (G2 > 0) ? 1 : (G1 > 0) ? -1 : 0;
  }

  function showBottomRight() {
    return gridValue !== 0 && (C3 > 0) ? 1 : 0;
  }

  function round(value: number, precision: number): number {
    if (precision === 0) return Math.round(value);
    return Math.round(value * (10 * precision)) / (10 * precision);
  }

  const renderStatistic = React.useCallback(
    (title, lines, icon) => {
      return (
        <>
          <Grid display="flex" alignItems="center" p={2} gap={2}
                borderBottom={`1px solid rgb(237, 237, 238)`}>
            <Grid item>
              {icon}
            </Grid>
            <Grid display="flex" flexDirection="column" item>
              <Box display="flex" flexDirection="column">
                <Typography fontWeight="400" fontSize="0.75em">{title}</Typography>
                <Box display="flex" flexDirection="column">
                  {
                    lines
                      .map((line: string) => {
                        if (line == null) return
                        let key = typeof line === 'object' ? (line as any).key : line
                        return (<Typography key={key} fontSize="0.85em">{line}</Typography>)
                      })
                  }
                </Box>
              </Box>
            </Grid>
          </Grid>
        </>
      )
    }, [])

  function format(dateTime) {
    let day: string = dateTime?.getDate() > 9 ? dateTime?.getDate() : `0${dateTime?.getDate()}`;
    let month: string = dateTime?.getMonth() + 1 > 9 ? dateTime?.getMonth() + 1 : `0${dateTime?.getMonth() + 1}`;
    let year = dateTime?.getFullYear();
    let hours = dateTime?.getHours() > 9 ? dateTime?.getHours() : `0${dateTime?.getHours()}`;
    let minutes = dateTime?.getMinutes() > 9 ? dateTime?.getMinutes() : `0${dateTime?.getMinutes()}`;
    let seconds = dateTime?.getSeconds() > 9 ? dateTime?.getSeconds() : `0${dateTime?.getSeconds()}`;
    return dateTime?.getDate() !== undefined ? `${day}/${month}/${year} ${hours}:${minutes}:${seconds}` : '-';
  }

  function changeDate(date: any): void {
    setSelectedDate(date);
  }

  function formatOptional(value: string): string {
    return value ? value : "-";
  }

  function getOptimizerDisplayText(optimizer: string): string {
    if (!optimizer) return '-';
    if (optimizer === 'EPEX') return t('dashboard.optimizerValues.epex');
    if (optimizer === 'DISABLED') return t('dashboard.optimizerValues.disabled');
    if (optimizer === 'SELF-CONS') return t('dashboard.optimizerValues.selfConsumption');
    return t('dashboard.optimizerValues.imbalance');
  }

  function renderOptimizerStatus() {
    if (isOptimizerFetching || !confOptimizer || !actOptimizer) {
      return <b>-</b>;
    }

    // If configured and active are the same, show the value directly
    if (confOptimizer === actOptimizer) {
      return <b>{getOptimizerDisplayText(confOptimizer)}</b>;
    }

    // If they differ, show configured with strikethrough → arrow → active
    return (
      <b>
        <span style={{ textDecoration: 'line-through' }}>
          {getOptimizerDisplayText(confOptimizer)}
        </span>
        {' → '}
        {getOptimizerDisplayText(actOptimizer)}
      </b>
    );
  }

  return <>
    <Card xs={12} lg={6} sx={{display: 'flex', justifyContent: 'center', alignItems: 'center'}}>
      <Box display="flex" flexDirection="column"
           width={isMobile ? "100%" : "70%"}
           sx={{color: (theme: any) => theme.palette.text.primary}}
           gap={1}>
        <Box display="flex" flexDirection="column">
          <Typography fontWeight="700" fontSize={isMobile ? "1.5em" : "2em"}>{activeSmartBox?.account}</Typography>
          <Typography fontWeight="700" fontSize={isMobile ? "0.9em" : "1em"}>{activeSmartBox?.serialId}</Typography>
        </Box>
        {renderStatistic(t('common.address'), [<span key="address"><b>{`${formatOptional(activeSmartBox?.address?.street)}, ${formatOptional(activeSmartBox?.address?.city)}`}</b></span>],
          <img width="30px" src={LocationSvg}/>)}
        {renderStatistic(t('common.battery'), [<span
            key="capacity">{t('dashboard.batteryCapacity')}: <b>{activeSmartBox?.properties?.batteryCapacity ? activeSmartBox?.properties?.batteryCapacity + ' kWh' : '-'}</b></span>,
            <span
              key="power">{t('dashboard.inverterPower')}: <b>{activeSmartBox?.properties?.inverterPower ? activeSmartBox?.properties?.inverterPower + ' kW' : '-'}</b></span>],
          <img width="30px" src={BatterySvg}/>)}
        {renderStatistic(t('dashboard.liveStatus'), [<span
          key="optimizerControl">{t('dashboard.optimizer')} : {renderOptimizerStatus()}</span>,
          <span
          key="stateOfCharge">{t('dashboard.stateOfCharge')} : <b>{live?.soc ? live.soc + '%' : '-'}</b></span>,
          <span
            key="charge">{t('common.status')}: <b>{live?.flex > 0 ? t('dashboard.statuses.charging') : live?.flex < 0 ? t('dashboard.statuses.discharging') : '-'}</b></span>,
          <span
            key="updateTimestamp">{t('dashboard.batteryLastUpdate')}: <b>{format(liveUpdateTime)}</b></span>
        ], <img width="30px" src={LiveSvg}/>)}
      </Box>
    </Card>
    <Card xs={12} lg={6}
          sx={{display: 'flex', justifyContent: 'center', alignItems: 'center', padding: isMobile ? 3 : 5}}>
      <Grid height="100%" item>
        <FlowAnimation topValue={productionValue}
                       leftValue={flexValue}
                       rightValue={consumptionValue}
                       bottomValue={gridValue}
                       vertical={showVertical()}
                       horizontal={showHorizontal()}
                       topLeft={showLeftTop()}
                       topRight={showRightTop()}
                       bottomLeft={showBottomLeft()}
                       bottomRight={showBottomRight()}
                       viewRatio={1}
        />
      </Grid>
    </Card>
    <Grid pl={isMobile ? 1 : 2} pr={isMobile ? 1 : 2} xs={12} item>
      {/* <Divider/> */}
    </Grid>
    <Grid xs={12} lg={12} pl={2} pr={2} pt={3} pb={3} sx={{backgroundColor: theme.palette.background.dashboard}} item>
      <Box display="flex" justifyContent="center" alignItems="center" gap={1}>
        <IconButton onClick={goToPreviousDay} size="small" sx={{color: theme.palette.text.tertiary}}>
          <ChevronLeftIcon />
        </IconButton>
        <CustomProvider locale={{...nlNL, Plaintext: {...nlNL.Plaintext, notSelected: "Datum"}}}>
          <RangeChip label={
            <Box display="flex" alignItems="center" gap={1}>
              <img width="12px" src={CalendarSvg}/>
              <DatePicker plaintext shouldDisableDate={afterToday()} open={pickerOpen}
                          onExit={() => setPickerOpen(false)} onClose={() => setPickerOpen(false)}
                          onOk={() => setPickerOpen(false)} onClean={() => {
                setSelectedDate(new Date())
              }} value={selectedDate} onChange={changeDate}
                          placeholder="Selecteer datum"
                          placement="bottom">

              </DatePicker>
            </Box>
          } selected={'true'}
                     isSelected={true} onClick={() => {
            if (!pickerOpen) {
              setPickerOpen(true)
            }
          }}/>
        </CustomProvider>
        <IconButton onClick={goToNextDay} size="small" sx={{color: isToday(selectedDate) ? '#C2C2C2' : theme.palette.text.tertiary}}>
          <ChevronRightIcon />
        </IconButton>
      </Box>
    </Grid>
    <Statistics/>
    <Card xs={12} lg={6} sx={{padding: isMobile ? 4 : 5}}>
      <ChartCard title={t('dashboard.technical.offtakeNet')} info={t('dashboard.technical.offtakeNetHint')} loading={isFetching.current ? 1 : 0}>
        <Box sx={{height: '100%', minHeight: 250}}>
          {((!data || !data.readings || data.readings.length == 0) && !isFetching) && <Typography fontSize={12}>No data available</Typography>}
          {(data && data.readings && data.readings.length > 0) && <GridChart/>}
        </Box>
      </ChartCard>
    </Card>
    <Card xs={12} lg={6} sx={{padding: isMobile ? 4 : 5}}>
      <ChartCard title={t(isEpexControlled ? 'dashboard.technical.epex' : 'dashboard.technical.imbalanceEpex')} info={t(isEpexControlled ? 'dashboard.technical.epexHint' : 'dashboard.technical.imbalanceEpexHint')} loading={isFetching.current ? 1 : 0}>
        <Box sx={{height: '100%', minHeight: 250}}>
          {((!data || !data.readings || data.readings.length == 0) && !isFetching) && <Typography fontSize={12}>No data available</Typography>}
          {(pricesData && pricesData.readings && pricesData.readings.length > 0) && <ImbalancePricesChart data={pricesData} isEpexControlled={isEpexControlled}/>}
        </Box>
      </ChartCard>
    </Card>
    <Card xs={12} lg={6} sx={{padding: isMobile ? 4 : 5}}>
      <ChartCard title={t('dashboard.technical.consumption')} info={t('dashboard.technical.consumptionHint')} loading={isFetching.current ? 1 : 0}>
        <Box sx={{height: '100%', minHeight: 250}}>
          {((!data || !data.readings || data.readings.length == 0) && !isFetching) && <Typography fontSize={12}>No data available</Typography>}
          {(data && data.readings && data.readings.length > 0) && <ConsumptionChart/>}
        </Box>
      </ChartCard>
    </Card>
    <Card xs={12} lg={6} sx={{padding: isMobile ? 4 : 5}}>
      <ChartCard title={t('dashboard.technical.batteryActions')} info={t('dashboard.technical.batteryActionsHint')} loading={isFetching.current ? 1 : 0}>
        <Box sx={{height: '100%', minHeight: 250}}>
          {((!data || !data.readings || data.readings.length == 0) && !isFetching) && <Typography fontSize={12}>No data available</Typography>}
          {(data && data.readings && data.readings.length > 0) && <FlexChart/>}
        </Box>
      </ChartCard>
    </Card>
    <Card xs={12} lg={6} sx={{padding: isMobile ? 4 : 5}}>
      <ChartCard title={t('dashboard.technical.production')} info={t('dashboard.technical.productionHint')} loading={isFetching.current ? 1 : 0}>
        <Box sx={{height: '100%', minHeight: 250}}>
          {((!data || !data.readings || data.readings.length == 0) && !isFetching) && <Typography fontSize={12}>No data available</Typography>}
          {(data && data.readings && data.readings.length > 0) && <ProductionChart/>}
        </Box>
      </ChartCard>
    </Card>
    <Card xs={12} lg={6} sx={{padding: isMobile ? 4 : 5}}>
      <ChartCard title={t('dashboard.technical.chargeLevelBattery')} info={t('dashboard.technical.chargeLevelBatteryHint')} loading={isFetching.current ? 1 : 0}>
        <Box sx={{height: '100%', minHeight: 250}}>
          {((!data || !data.readings || data.readings.length == 0) && !isFetching) && <Typography fontSize={12}>No data available</Typography>}
          {(data && data.readings && data.readings.length > 0) && <SOCChart/>}
        </Box>
      </ChartCard>
    </Card>
  </>
}